/**
 * My Data Page — the user's files, full width.
 *
 * Holder-aware: the HolderSwitcher at the top lets users toggle between
 * their Personal Holder, any Org workspace, and guild shared spaces. Each
 * renders the Vault workspace (`VaultWorkspacePage` — Desktop, Browse and
 * Search over the same files), whose Storage panel also shows what arches
 * keep besides files, so no store the user pays for is out of sight.
 *
 * URL keeps `/my-data`; the selected holder mirrors into a
 * `?holder=<id>` query param for shareability (mirrors Fias 360's
 * convention). `?folderPath=` deep-links into a folder and `?view=` picks a
 * view — both handled by the workspace.
 *
 * Expands to full screen like an arche (focus mode), sharing the Vault
 * Documents arche's state — it is the same workspace. Expanded, this page's
 * header and the platform Header go; the way back (Fias, Worktop, Fias AI,
 * Collapse) and the workspace switcher — the platform Header's own chip,
 * which went with the Header — move into the workspace's toolbar, beside its
 * view tabs, so everything stays on one row. The star adds it to
 * the Worktop (as the Vault Documents arche — the Worktop holds arches).
 */

import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { VaultWorkspacePage } from '@/arche/arches/vault-documents/pages/VaultWorkspacePage';
import { ARCHE_ID as VAULT_DOCUMENTS_ARCHE_ID } from '@/arche/arches/vault-documents/constants';
import { ArcheExpandButton } from '@/arche/components/ArcheExpandButton';
import { ExpandedArcheControls } from '@/arche/components/ExpandedArcheHeader';
import { FavoriteButton } from '@/arche/components/FavoriteButton';
import { useChrome } from '@/arche/context/ChromeContext';
import { HolderSwitcher, HolderSwitcherChip, useHolderSwitcher } from '@/components/HolderSwitcher';

export function MyDataPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  // Holder switcher — defaults to Personal on first load; mirrors
  // the active holder into ?holder=<id> for shareability.
  // includeGuilds: documents are guild-enabled in V1, so guild shared data
  // spaces are selectable here.
  const switcher = useHolderSwitcher({ defaultToPersonal: true, includeGuilds: true });
  const urlHolder = searchParams.get('holder');
  useEffect(() => {
    if (urlHolder && urlHolder !== switcher.selectedHolderId) {
      switcher.setSelectedHolderId(urlHolder);
    }
  }, [urlHolder]);
  useEffect(() => {
    if (!switcher.selectedHolderId) return;
    if (searchParams.get('holder') === switcher.selectedHolderId) return;
    const next = new URLSearchParams(searchParams);
    next.set('holder', switcher.selectedHolderId);
    setSearchParams(next, { replace: true });
  }, [switcher.selectedHolderId]);

  const expanded = useChrome().isHeadersHidden(VAULT_DOCUMENTS_ARCHE_ID);
  const holderSwitcher = (
    <HolderSwitcher
      selectedHolderId={switcher.selectedHolderId}
      onChange={(next) => switcher.setSelectedHolderId(next)}
      includeGuilds
    />
  );

  // Expanded: the Header's workspace chip (width-capped, and no "+ New
  // workspace" — a settings action, not a toolbar one; the selection is shared,
  // so switching there switches here), and the focus-mode way back beside the
  // view tabs.
  const compactSwitcher = <HolderSwitcherChip />;
  const focusControls = ({ showLabels }: { showLabels: boolean }) => (
    <ExpandedArcheControls
      archeId={VAULT_DOCUMENTS_ARCHE_ID}
      labelClassName={showLabels ? '' : 'hidden'}
    />
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {!expanded && (
        <div className="border-b border-border bg-card">
          <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-4 sm:px-6">
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-2xl font-bold text-foreground">My Data</h1>
                <FavoriteButton archeId={VAULT_DOCUMENTS_ARCHE_ID} />
                <ArcheExpandButton archeId={VAULT_DOCUMENTS_ARCHE_ID} />
              </div>
              <p className="mt-1 text-sm text-muted-foreground">
                {switcher.selectedOption?.holderType === 'org'
                  ? `Workspace documents for ${switcher.selectedOption.displayName}`
                  : switcher.selectedOption?.holderType === 'guild'
                    ? `Shared guild documents for ${switcher.selectedOption.displayName}`
                    : 'Your personal documents and AI-generated content'}
              </p>
            </div>
            {holderSwitcher}
          </div>
        </div>
      )}

      {!switcher.selectedHolderId ? (
        <>
          {/* Expanded, this is the only way back when no workspace is open. */}
          {expanded && (
            <div className="flex justify-end border-b border-border bg-card/40 px-4 py-2">
              {focusControls({ showLabels: true })}
            </div>
          )}
          <div className="flex flex-col items-center gap-3 px-4 py-12 text-center text-sm text-muted-foreground sm:px-6">
            <p>Choose a workspace {expanded ? 'here' : 'above'} to see your documents.</p>
            {expanded && compactSwitcher}
          </div>
        </>
      ) : (
        <VaultWorkspacePage
          holderId={switcher.selectedHolderId}
          holder={switcher.selectedOption}
          toolbarExtra={expanded ? compactSwitcher : undefined}
          toolbarEnd={expanded ? focusControls : undefined}
        />
      )}
    </div>
  );
}

export default MyDataPage;
